/**
 * Pure cache transforms for optimistic pipeline drag-and-drop.
 *
 * Kept free of React and of the query client so the board's drop handlers can
 * apply a move to cached data the moment the card is released, and roll it back
 * untouched if the server rejects it.
 */
import type { Candidate, JobPipeline } from '../models/job';

export interface OptimisticCardMove {
  candidateId: string;
  toStageId: string;
  /** Stage semantic of the destination column. */
  toStageType?: string;
  /**
   * Candidate the card was dropped below, null when dropped at the top, or
   * undefined to append at the end (no drop position).
   */
  insertAfterCandidateId?: string | null;
}

/**
 * Ranks handed to a column while the server's LexoRanks are in flight.
 *
 * Every card in the destination column is re-ranked together, so these only
 * ever compare against each other and are replaced on the next refetch.
 */
function optimisticStagePosition(index: number): string {
  return String(index).padStart(6, '0');
}

/** Mirrors the board's column resolution without needing the stage list. */
function isInStage(
  candidate: Candidate,
  stageId: string,
  stageType?: string
): boolean {
  if (candidate.pipelineStageId) return candidate.pipelineStageId === stageId;
  return Boolean(stageType) && candidate.pipelineStageSemantic === stageType;
}

/** Sort order of cards inside one column: server rank first, then name. */
export function compareCandidatesInColumn(a: Candidate, b: Candidate): number {
  const aPos = a.stagePosition ?? '';
  const bPos = b.stagePosition ?? '';
  if (aPos && bPos && aPos !== bPos) return aPos < bPos ? -1 : 1;
  return (a.talentName || '').localeCompare(b.talentName || '');
}

/**
 * Sort order of board items: grouped by column, then by in-column order.
 * Cross-column pairs must not compare as equal, or `Array.sort` can skip
 * comparing two cards of the same column that are not adjacent.
 */
export function compareBoardItems(
  a: { columnId: string; candidate: Candidate },
  b: { columnId: string; candidate: Candidate }
): number {
  if (a.columnId !== b.columnId) return a.columnId < b.columnId ? -1 : 1;
  return compareCandidatesInColumn(a.candidate, b.candidate);
}

/** Move a card to a stage/position, re-ranking the destination column. */
export function applyOptimisticCardMove(
  candidates: Candidate[] | undefined,
  move: OptimisticCardMove
): Candidate[] | undefined {
  if (!candidates) return candidates;

  const moved = candidates.find(
    (candidate) => candidate.id === move.candidateId
  );
  if (!moved) return candidates;

  const movedNext: Candidate = {
    ...moved,
    pipelineStageId: move.toStageId,
    pipelineStageSemantic: move.toStageType ?? moved.pipelineStageSemantic,
  };

  const column = candidates
    .filter(
      (candidate) =>
        candidate.id !== move.candidateId &&
        isInStage(candidate, move.toStageId, move.toStageType)
    )
    .sort(compareCandidatesInColumn);

  const anchorIndex = move.insertAfterCandidateId
    ? column.findIndex(
        (candidate) => candidate.id === move.insertAfterCandidateId
      )
    : -1;
  const insertAt =
    move.insertAfterCandidateId === null
      ? 0
      : anchorIndex >= 0
        ? anchorIndex + 1
        : column.length;
  column.splice(insertAt, 0, movedNext);

  const ranks = new Map(
    column.map((candidate, index) => [
      candidate.id,
      optimisticStagePosition(index),
    ])
  );

  return candidates.map((candidate) => {
    const next = candidate.id === move.candidateId ? movedNext : candidate;
    const rank = ranks.get(next.id);
    return rank ? { ...next, stagePosition: rank } : next;
  });
}

/** Move a stage to a 1-based position and renumber the pipeline. */
export function applyOptimisticStageOrder(
  pipeline: JobPipeline | undefined,
  move: { stageId: string; position: number }
): JobPipeline | undefined {
  if (!pipeline) return pipeline;

  const stages = [...pipeline.stages].sort((a, b) => a.position - b.position);
  const from = stages.findIndex((stage) => stage.id === move.stageId);
  if (from < 0) return pipeline;

  const to = Math.min(Math.max(move.position - 1, 0), stages.length - 1);
  if (from === to) return pipeline;

  const [moved] = stages.splice(from, 1);
  stages.splice(to, 0, moved);

  return {
    ...pipeline,
    stages: stages.map((stage, index) => ({ ...stage, position: index + 1 })),
  };
}
